// Helpers puros do painel para promocoes automaticas e feed de ofertas:
// estado da credencial, mensagens de erro das RPCs, texto da oferta pelo
// template do tenant e formatacao dos numeros do card.

import {
  buildTemplateVars,
  renderTemplate,
  resolveTemplateBody,
  templateRefFor,
  type CredentialState,
  type ForwardingSettings,
  type UserTemplate,
} from "@lucralink/shared";
import type { AffiliateCredentialPublic } from "@/lib/types";

/** Estado da credencial para o checklist (view mascarada, sem segredo). */
export function credentialStateOf(credential: AffiliateCredentialPublic | undefined): CredentialState {
  if (!credential) return "missing";
  if (credential.needs_reauth || credential.status === "invalid") return "invalid";
  return credential.status === "valid" ? "valid" : "incomplete";
}

const ERROR_MESSAGES: Readonly<Record<string, string>> = {
  auto_promotion_destination_limit_reached:
    "Você passou do limite de destinos do seu plano. Desmarque algum ou faça upgrade.",
  auto_promotion_provider_unavailable:
    "Essa loja ainda não tem fonte oficial de ofertas. A curadoria não pode ser ligada.",
  auto_promotion_credential_missing: "Cadastre e valide a credencial da loja antes de ligar.",
  auto_promotion_categories_missing: "Escolha pelo menos uma categoria antes de ligar.",
  auto_promotion_destinations_missing: "Marque pelo menos um destino antes de ligar.",
  invalid_destinations: "Um dos destinos escolhidos não está mais disponível.",
  invalid_categories: "Revise as categorias escolhidas.",
  invalid_auto_promotion_config: "Revise os números: algum valor está fora da faixa permitida.",
  tenant_not_found: "Não encontramos sua empresa. Entre novamente.",
};

/** Traduz o erro da RPC em texto para o usuario. */
export function autoPromotionErrorMessage(error: unknown): string {
  const raw =
    typeof error === "object" && error !== null && "message" in error
      ? String((error as { message: unknown }).message)
      : "";
  const key = Object.keys(ERROR_MESSAGES).find((k) => raw.includes(k));
  return key ? ERROR_MESSAGES[key]! : "Não foi possível salvar. Tente de novo em instantes.";
}

/** Campo numerico opcional: vazio = sem filtro (null), invalido = NaN. */
export function parseOptionalNumber(input: string): number | null {
  const value = input.trim().replace(",", ".");
  if (value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : Number.NaN;
}

export function formatOptionalNumber(value: number | null): string {
  return value === null ? "" : String(value).replace(".", ",");
}

export interface OfferTextInput {
  readonly title: string;
  readonly priceCents: number | null;
  readonly originalPriceCents: number | null;
  readonly discountPercent: number | null;
}

/** Texto da oferta pelo template do tenant para a loja (texto puro, sem marcacao). */
export function composeOfferText(
  offer: OfferTextInput,
  affiliateUrl: string,
  store: string,
  settings: ForwardingSettings,
  templates: readonly UserTemplate[],
): string {
  const ref = templateRefFor(store, settings.defaultTemplateRef, settings.templateByProvider);
  const vars = buildTemplateVars({ ...offer, coupon: null }, affiliateUrl, store);
  return renderTemplate(resolveTemplateBody(ref, templates), vars, "plain");
}

const RELATIVE = new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" });

/** "há 5 minutos", "em 2 horas". */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const diffMinutes = Math.round((new Date(iso).getTime() - now) / 60_000);
  if (Math.abs(diffMinutes) < 60) return RELATIVE.format(diffMinutes, "minute");
  const diffHours = Math.round(diffMinutes / 60);
  if (Math.abs(diffHours) < 48) return RELATIVE.format(diffHours, "hour");
  return RELATIVE.format(Math.round(diffHours / 24), "day");
}

/** 1 -> "a cada 1 hora", 30 -> "a cada 30 minutos". */
export function intervalLabel(minutes: number): string {
  if (minutes < 60) return `A cada ${minutes} minutos`;
  const hours = minutes / 60;
  return hours === 1 ? "A cada 1 hora" : `A cada ${hours} horas`;
}
